/**
 * The ordering rules behind a template's chip row: which fields are still offered, and what each
 * drag or tap does to the order.
 *
 * Extracted from `ProjectSettings.vue`, which held the only copy while the automatic project names
 * were the only template. The rules are transcribed unchanged, and
 * [../../tests/unit/templateFieldOrdering.spec.ts](../../tests/unit/templateFieldOrdering.spec.ts)
 * describes them as they behaved there, so both consumers can be checked against one behaviour.
 *
 * A rejected move returns `null` rather than a silently unchanged list: the caller has to say why
 * nothing happened, which is the snackbar the repeatable field's edge rule needs.
 *
 * `repeatable` is the one field that may appear more than once and never at either end (a comma).
 * Pass `null` for a template that has no such field.
 */

/** The fields not yet used. The repeatable one is always offered, and offered first. */
export function availableTemplateFields<T extends string>(
  all: T[],
  active: T[],
  repeatable: T | null
): T[] {
  const others = all.filter((field) => field !== repeatable && !active.includes(field));

  return repeatable === null ? others : [repeatable, ...others];
}

/**
 * Whether putting [field] at [index] would leave the repeatable field first or last.
 *
 * @param fields the list as it will be *before* the insertion, so a move must pass the list with the
 *   moved field already taken out.
 */
export function wouldPlaceRepeatableAtEdge<T extends string>(
  field: T,
  index: number,
  fields: T[],
  repeatable: T | null
): boolean {
  if (repeatable === null || field !== repeatable) {
    return false;
  }

  return index === 0 || index >= fields.length;
}

/** A field dragged from the available row onto the chip at [index]. */
export function insertTemplateField<T extends string>(
  fields: T[],
  field: T,
  index: number,
  repeatable: T | null
): T[] | null {
  if (wouldPlaceRepeatableAtEdge(field, index, fields, repeatable)) {
    return null;
  }

  const next = [...fields];
  next.splice(index, 0, field);

  return next;
}

/**
 * A chip dragged onto the chip at [to].
 *
 * The target index is read against the list with the dragged field removed, which is why dropping a
 * field on the position after itself is not a move at all.
 */
export function moveTemplateField<T extends string>(
  fields: T[],
  from: number,
  to: number,
  repeatable: T | null
): T[] | null {
  // Not a rejection: dropping a chip on itself is nothing happening, and null here would make the
  // caller report a rule the crew did not break.
  if (from === to) {
    return [...fields];
  }

  const next = [...fields];
  const [moved] = next.splice(from, 1);
  const targetIndex = to > from ? to - 1 : to;

  if (wouldPlaceRepeatableAtEdge(moved, targetIndex, next, repeatable)) {
    return null;
  }

  next.splice(targetIndex, 0, moved);

  return next;
}

/** A field dropped past the last chip, from the available row. */
export function appendTemplateField<T extends string>(
  fields: T[],
  field: T,
  repeatable: T | null
): T[] | null {
  if (repeatable !== null && field === repeatable) {
    return null;
  }

  return [...fields, field];
}

/** An existing chip dragged past the last chip. */
export function moveTemplateFieldToEnd<T extends string>(
  fields: T[],
  from: number,
  repeatable: T | null
): T[] | null {
  const next = [...fields];
  const [moved] = next.splice(from, 1);

  if (repeatable !== null && moved === repeatable) {
    return null;
  }

  next.push(moved);

  return next;
}

/**
 * A field tapped in the available row.
 *
 * The repeatable field goes *before* the last chip rather than after it, because appending it would
 * leave it at the end; with nothing configured yet there is no such position and the tap is refused.
 */
export function addTemplateField<T extends string>(
  fields: T[],
  field: T,
  repeatable: T | null
): T[] | null {
  if (repeatable !== null && field === repeatable) {
    if (fields.length === 0) {
      return null;
    }

    const next = [...fields];
    next.splice(next.length - 1, 0, field);

    return next;
  }

  return [...fields, field];
}

/**
 * A chip removed with its close button.
 *
 * Removing a field can leave the repeatable one exposed at either end, where it would print as a
 * leading or trailing separator, so any it exposes are dropped with it.
 */
export function removeTemplateField<T extends string>(
  fields: T[],
  index: number,
  repeatable: T | null
): T[] {
  const next = [...fields];
  next.splice(index, 1);

  if (repeatable === null) {
    return next;
  }

  while (next.length > 0 && next[0] === repeatable) {
    next.shift();
  }
  while (next.length > 0 && next[next.length - 1] === repeatable) {
    next.pop();
  }

  return next;
}

/**
 * The preview line for a template, written with sample values.
 *
 * Every sample value is present by definition, so this only has to collapse the space a separator
 * inherits from the join. Real data goes through its own renderer, which also has to survive parts
 * that are missing.
 */
export function renderTemplateSample<T extends string>(
  fields: T[],
  sampleValues: Record<string, string>
): string {
  return fields
    .map((field) => sampleValues[field])
    .join(" ")
    .replace(/ ,/g, ",");
}
